// Lib
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { getElementId } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { length } from '../../../../../../common/utils/immutableHelper';
import { getColorDisplayValue } from '../../../../../../common/colors/elementColorFormatUtil';
import { getThemeIsDarkMode } from '../../../../../user/account/accountModal/interface/themeSettings/themeSelector';
import { getBackgroundColorHex } from '../../../../../element/table/utils/tableCellFormattingUtils';
import {
    selectActiveTableCellSelection,
    selectActiveTableCurrentCellBackgroundColor,
} from '../../../../../element/table/store/selectors/activeHotTableSelectors';

// Actions
import { updateTableCellBackgroundColor } from '../../../../../element/table/actions/hotTableActions';
import { setHotTableCellBackgroundColor } from '../../../../../element/table/utils/hotInstanceUtils/hotTableCellMetaUpdateUtils';

// Components
import ToolbarPopupTool from '../../ToolbarPopupTool';
import DesktopColorPopup from '../colorTools/popup/desktop/DesktopColorPopup';
import ColorToolIcon from '../colorTools/ColorToolIcon';
import { useActiveTableCallback, useActiveTableSelector } from '../../../../../element/table/store/hotTableStoreHooks';

// Constants
import { PopupIds, PopupSections } from '../../../../../components/popupPanel/popupConstants';
import {
    TABLE_DEFAULT_COLORS,
    TABLE_DEFAULT_HEADER_BACKGROUND_COLOR,
} from '../../../../../../common/table/tableConstants';
import { CellSelections } from '../../../../../../common/table/TableTypes';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

// Styles
import './TableCellColorTool.scss';

type ColorToolPopupIconProps = {
    selectedColor: string;
    isPopupOpen: boolean;
    currentCellSelections: CellSelections;
};

const ColorToolPopupIcon: React.FC<ColorToolPopupIconProps> = ({
    selectedColor,
    isPopupOpen,
    currentCellSelections,
}) => {
    const color =
        currentCellSelections?.length > 0
            ? getColorDisplayValue(selectedColor)
            : 'var(--ui-toolbar-tool-disabled-foreground)';
    const selectedColorDisplayValue =
        selectedColor === TABLE_DEFAULT_HEADER_BACKGROUND_COLOR ? 'var(--ws-table-header-row-background)' : color;

    return <ColorToolIcon color={selectedColorDisplayValue} isActive={isPopupOpen} />;
};

type TableCellColorToolProps = {
    selectedElements: Immutable.List<ImMNElement>;
};

const TableCellColorTool: React.FC<TableCellColorToolProps> = (props) => {
    const { selectedElements } = props;

    const isDarkMode = useSelector(getThemeIsDarkMode);
    const currentCellSelections = useActiveTableSelector(selectActiveTableCellSelection);

    const elementId = getElementId(selectedElements.first());

    const selectedColor = useActiveTableSelector(selectActiveTableCurrentCellBackgroundColor);

    const dispatch = useDispatch();

    const updateCellTextAlignment = useActiveTableCallback(
        (activeTable, backgroundColor) => {
            dispatch(
                updateTableCellBackgroundColor({
                    hotInstance: activeTable,
                    elementId,
                    backgroundColor,
                }),
            );
        },
        [elementId],
    );

    const updateTemporaryCellColor = useActiveTableCallback(
        (activeTable, tempColor) => setHotTableCellBackgroundColor(activeTable, undefined, tempColor),
        [],
    );

    return (
        <ToolbarPopupTool
            {...props}
            name="Cell color"
            buttonSelector=".ColorTool .ColorToolIcon"
            popupId={PopupIds.COLOR}
            closeSection={PopupSections.TOOLBAR}
            toolName="color"
            disabled={length(currentCellSelections) === 0}
            // @ts-ignore Struggling with the JSX type inference
            className="table-cell-color-tool"
            customColorHandler={updateCellTextAlignment}
            IconComponent={ColorToolPopupIcon}
            PopupComponent={DesktopColorPopup}
            selectedColor={selectedColor}
            selectedColorHex={getBackgroundColorHex(selectedColor, isDarkMode)}
            customDefaultColors={Object.values(TABLE_DEFAULT_COLORS)}
            customTempColorHandler={updateTemporaryCellColor}
            currentCellSelections={currentCellSelections}
            preventFocus
        />
    );
};

export default TableCellColorTool;
